import React, { useEffect, useState } from "react";
import { Button, Col, Container, Form, Row, FormSelect } from "react-bootstrap";

// css
import styles from "./addForm.module.scss";
import { Formik } from "formik";
import addProgramSchema from "./addProgram.schema";
import { cloneDeep } from "lodash";
import { successMessageToast } from "helpers/common";
import { useNavigate, useParams, Link } from "react-router-dom";
import AppInputField from "components/shared/FormComponents/AppInputField";
import { DAY_OPTIONS, LOCATION_TYPE_OPTIONS } from "config/constants";
import { CommonMultiSelect, CommonSelect, RequiredFieldAstrick } from "components/Common/Common";
import { useGetFundingSourceListQuery, useGetLessonsListQuery, useTherapistLookupQuery } from "store/services/utility";
import AppSelectField from "components/shared/FormComponents/AppSelectField";
import { useChatterProgramDetailsQuery, useCreateChatterProgramMutation, useUpdateChatterProgramMutation } from "store/services/chatter/program";
import LoadingIndicator from "components/shared/LoadingIndicator";
import { CHATTER_PROGRAM_LIST } from "config/paths";
import AppDatePicker from "components/shared/FormComponents/AppDatePicker";

const AddChatterProgramFiles = () => {

  const { id: programId } = useParams();
  const editMode = !!programId;

  const navigate = useNavigate();

  const [initialValues, setInitialValues] = useState({
    name: "",
    start_date: "",
    end_date: "",
    days: [],
    time: "",
    location: "",
    therapist_head1: "",
    therapist_head2: "",
    funding_source: "",
    location_type: "",
    status: "active",
    lessons: []
  });

  // Therapist list
  const { data: therapistList } = useTherapistLookupQuery();
  const therapitsOptions = therapistList?.map(item => ({ value: item.id, label: item?.firstname + " " + item?.lastname }));

  // Lesson list
  const { data: lessons, isLoading: lessonsLoading, isFetching: lessonsFetching } = useGetLessonsListQuery({ page: 1, limit: 500 });
  const lessonsList = lessons?.records || [];

  const lessonOptions = lessonsList?.map(item => ({ value: item.id, label: item?.title }));

  // Funding source list
  const { data: fundingSourceList } = useGetFundingSourceListQuery({ page: 1, limit: 100 });
  const fundingSourceOptions = fundingSourceList?.map(item => ({ value: item.id, label: item?.name }));

  const [createProgram, { isLoading: createProgramLoading }] = useCreateChatterProgramMutation();
  const [updateProgram, { isLoading: updateProgramLoading }] = useUpdateChatterProgramMutation();
  const { data: programDetails, isLoading } = programId ? useChatterProgramDetailsQuery(programId) : {};

  const handleSubmit = async (payload) => {

    try {

      let values = cloneDeep(payload);

      // format values
      values.funding_source = parseInt(payload?.funding_source?.value);
      values.therapist_head1 = parseInt(payload?.therapist_head1?.value);
      values.therapist_head2 = parseInt(payload?.therapist_head2?.value);
      values.therapist_head3 = parseInt(payload?.therapist_head3?.value);
      values.location_type = payload?.location_type?.value;
      values.days = payload?.days?.map(d => d.value) || [];
      values.lessons = payload.lessons?.map(i => parseInt(i.value)) || [];

      const result = editMode ? await updateProgram({ data: values, programId }) : await createProgram(values);

      if (result?.error) {
        console.error("Error creating chatter program", result.error);
      } else {
        successMessageToast(result?.data?.message);
        navigate(CHATTER_PROGRAM_LIST);
      }
    } catch (err) {
      console.error("Error creating chatter program", err);
    }
  };

  const handleCancel = () => {

    navigate(CHATTER_PROGRAM_LIST);
  };

  useEffect(() => {

    if (!programId) return;

    setInitialValues({
      ...initialValues,
      name: programDetails?.name,
      funding_source: fundingSourceOptions?.find(item => item.value === programDetails?.funding_source),
      location: programDetails?.location,
      location_type: LOCATION_TYPE_OPTIONS?.find(item => item.value === programDetails?.location_type),
      therapist_head1: therapitsOptions?.find(item => item.value === programDetails?.therapist_head1),
      therapist_head2: therapitsOptions?.find(item => item.value === programDetails?.therapist_head2),
      therapist_head3: therapitsOptions?.find(item => item.value === programDetails?.therapist_head3),
      lessons: lessonOptions?.filter(item => programDetails?.lessonProgramMapping?.map(item => item?.lesson_id)?.includes(item.value)),
      days: DAY_OPTIONS?.filter(item => programDetails?.days?.includes(item.value)),
      time: programDetails?.time?.slice(0, 5),
      status: programDetails?.status,
      start_date: programDetails?.start_date,
      end_date: programDetails?.end_date,
    })
    console.log("init", initialValues)

  }, [therapistList,programDetails]);
  return (
    <>
      <section className={`${styles.addForm} position-relative py-4 addForm`}>
        <Container fluid={true}>
          <Row>
            <Col lg="12" className="my-2">
              <div className="pageHead pb-3 d-flex align-items-center">
                <Link
                  className="themeBlue fw-sbold back-to-btn"
                  to="/manage-chatter/program-profiles/"
                >
                  <svg width="20" height="14" viewBox="0 0 20 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M6 12L0 6L6 0L7.425 1.4L2.825 6L7.425 10.6L6 12ZM18 14V10C18 9.16667 17.7083 8.45833 17.125 7.875C16.5417 7.29167 15.8333 7 15 7H8.825L12.425 10.6L11 12L5 6L11 0L12.425 1.4L8.825 5H15C16.3833 5 17.5625 5.4875 18.5375 6.4625C19.5125 7.4375 20 8.61667 20 10V14H18Z" fill="#1C1B1F" />
                  </svg> Back
                </Link>
                <h6 className="m-0 fw-bold themeBlue">{editMode ? "Edit Chatter Group" : "Add New Program"}</h6>
              </div>
            </Col>
            {
              isLoading ? <LoadingIndicator /> :
                <Col lg="12" className="my-2">
                  <Formik
                    initialValues={initialValues}
                    validationSchema={addProgramSchema}
                    onSubmit={handleSubmit}
                    enableReinitialize
                  >
                    {({
                      handleSubmit,
                      values,
                      errors
                    }) => (
                      <Form onSubmit={handleSubmit}>
                        <div
                          className="py-3"
                          style={{ borderTop: "1px solid #41a2a84d" }}
                        >
                          {console.log("values of formik", values)}
                          {console.log("errors of formik", errors)}
                          <Row>
                            <Col md="4" sm="6" className="my-2">
                              <label
                                htmlFor=""
                                className="form-label themeBlue fw-bold mb-0d-flex"
                              >
                                Program Name
                                <RequiredFieldAstrick />
                              </label>
                              <AppInputField
                                name="name"
                                type="text"
                                placeholder="Enter name"
                                className={`${styles.formControl} form-control`}
                                maxLength={50}
                              />
                            </Col>
                            <Col md="4" sm="6" className="my-2">
                              <label
                                htmlFor=""
                                className="form-label themeBlue fw-bold mb-0d-flex"
                              >
                                Event Start Date
                                <RequiredFieldAstrick />
                              </label>
                              <AppDatePicker
                                name="start_date"
                                className={`${styles.formControl} form-control`}
                              />
                            </Col>
                            <Col md="4" sm="6" className="my-2">
                              <label
                                htmlFor=""
                                className="form-label themeBlue fw-bold mb-0d-flex"
                              >
                                Event End Date
                                <RequiredFieldAstrick />
                              </label>
                              <AppDatePicker
                                name="end_date"
                                className={`${styles.formControl} form-control`}
                              />
                            </Col>
                            <Col md="4" sm="6" className="my-2">
                              <label
                                htmlFor=""
                                className="form-label themeBlue fw-bold mb-0 d-flex"
                              >
                                Day
                                <RequiredFieldAstrick />
                              </label>
                              <CommonMultiSelect
                                name="days"
                                placeholder={"Please Select Day"}
                                option={DAY_OPTIONS}
                              />
                            </Col>
                            <Col md="4" sm="6" className="my-2">
                              <label
                                htmlFor=""
                                className="form-label themeBlue fw-bold mb-0 d-flex"
                              >
                                Event Start Time
                                <RequiredFieldAstrick />
                              </label>
                              <AppInputField
                                name="time"
                                type="time"
                                placeholder="00:00"
                                className={`${styles.formControl} form-control`}
                              />
                            </Col>
                            <Col md="4" sm="6" className="my-2">
                              <label
                                htmlFor=""
                                className="form-label themeBlue fw-bold mb-0 d-flex"
                              >
                                Location
                                <RequiredFieldAstrick />
                              </label>
                              <AppInputField
                                type="text"
                                name="location"
                                placeholder="Enter location"
                                className={`${styles.formControl} form-control`}
                                maxLength={50}
                              />
                            </Col>
                            <Col md="4" sm="6" className="my-2">
                              <label
                                htmlFor=""
                                className="form-label themeBlue fw-bold mb-0 d-flex"
                              >
                                Group Leader 1
                                <RequiredFieldAstrick />
                              </label>
                              <CommonSelect
                                name="therapist_head1"
                                placeholder={"Please select"}
                                option={therapitsOptions?.filter(x => x?.value != values?.therapist_head2?.value && x?.value != values?.therapist_head3?.value)}
                              />
                            </Col>
                            <Col md="4" sm="6" className="my-2">
                              <label
                                htmlFor=""
                                className="form-label themeBlue fw-bold mb-0 d-flex"
                              >
                                Group Leader 2
                                <RequiredFieldAstrick />
                              </label>
                              <CommonSelect
                                name="therapist_head2"
                                placeholder={"Please select"}
                                option={therapitsOptions?.filter(x => x?.value != values?.therapist_head1?.value && x?.value != values?.therapist_head3?.value)}
                              />
                            </Col>
                            <Col md="4" sm="6" className="my-2">
                              <label
                                htmlFor=""
                                className="form-label themeBlue fw-bold mb-0 d-flex"
                              >
                                Group Leader 3
                                <RequiredFieldAstrick />
                              </label>
                              <CommonSelect
                                name="therapist_head3"
                                placeholder={"Please select"}
                                option={therapitsOptions?.filter(x => x?.value != values?.therapist_head1?.value && x?.value != values?.therapist_head2?.value)}
                              />
                            </Col>
                            <Col md="4" sm="6" className="my-2">
                              <label
                                htmlFor=""
                                className="form-label themeBlue fw-bold mb-0 d-flex"
                              >
                                Location Type
                                <RequiredFieldAstrick />
                              </label>
                              <CommonSelect
                                name="location_type"
                                placeholder={"Location Type"}
                                option={LOCATION_TYPE_OPTIONS}
                              />
                            </Col>
                            <Col md="4" sm="6" className="my-2">
                              <label
                                htmlFor=""
                                className="form-label themeBlue fw-bold mb-0 d-flex"
                              >
                                Funding Source
                                <RequiredFieldAstrick />
                              </label>
                              <CommonSelect
                                name="funding_source"
                                placeholder={"Select Funding Source"}
                                option={fundingSourceOptions}
                              />
                            </Col>
                            <Col md="4" sm="6" className="my-2">
                              <label
                                htmlFor=""
                                className="form-label themeBlue fw-bold mb-0"
                              >
                                Status
                              </label>
                              <AppSelectField
                                name={"status"}
                                placeholder={"Please Select"}
                                className={`${styles.formControl} form-control`}
                                options={[
                                  {
                                    value: "active",
                                    label: "Active",
                                  },
                                  {
                                    value: "archived",
                                    label: "Archived",
                                  }
                                ]}
                              />
                            </Col>
                            <Col md="4" sm="6" className="my-2 common-multiselect">
                              <label
                                htmlFor=""
                                className="form-label themeBlue fw-bold mb-0"
                              >
                                Assign Chatter Lesson
                                {/* <RequiredFieldAstrick /> */}
                              </label>
                              <CommonMultiSelect
                                name="lessons"
                                type="select"
                                placeholder={"Chatter Lesson"}
                                option={lessonOptions}
                                isLoading={lessonsLoading || lessonsFetching}
                              />
                            </Col>
                          </Row>
                        </div>
                        <Row className="mt-4">
                          <Col className="my-2" lg="12">
                            <div
                              className={`${styles.btnWrpper} btnWrpper d-flex align-items-center gap-10 pt-3`}
                              style={{ borderTop: "1px solid #41a2a84d" }}
                            >
                              <Button disabled={createProgramLoading || updateProgramLoading} type="submit" className="d-flex align-items-center justify-content-center commonBtn">
                                {createProgramLoading || updateProgramLoading ? <LoadingIndicator inline /> : "Save"}
                              </Button>
                              <Button
                                type="button"
                                className="d-flex align-items-center justify-content-center commonBtn borderedBtn bg-transparent"
                                onClick={handleCancel}
                              >
                                Cancel
                              </Button>
                            </div>
                          </Col>
                        </Row>
                      </Form>
                    )}
                  </Formik>
                </Col>
            }
          </Row>
        </Container>
      </section>
    </>
  );
};

export default AddChatterProgramFiles;
